import { useState, useEffect } from 'react';
import { db } from '../firebase'; // Assuming firebase.js is correctly set up
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';

const useNotifications = (user) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user && user.id) {
      const fetchAndSaveNotifications = async () => {
        try {
          // Task Notifications
          const tasksRef = collection(db, 'assignedTasks');
          const taskQuery = query(tasksRef, where('employeeId', '==', user.id));
          const taskSnapshot = await getDocs(taskQuery);

          const taskNotifications = [];
          for (const docSnapshot of taskSnapshot.docs) {
            const data = docSnapshot.data();
            const notification = `New Task: ${data.taskName} assigned by ${data.assignedBy}`;
            const taskNotificationRef = collection(db, 'employeeTaskNotifications');
            const existingTaskQuery = query(
              taskNotificationRef,
              where('employeeId', '==', user.id),
              where('notification', '==', notification)
            );
            const existingTaskSnapshot = await getDocs(existingTaskQuery);
            if (existingTaskSnapshot.empty) {
              await addDoc(taskNotificationRef, {
                employeeId: user.id,
                notification,
                timestamp: serverTimestamp(),
                isRead: false,
              });
              taskNotifications.push(notification);
            }
          }

          // Leave Notifications
          const leaveRef = collection(db, 'employeeLeave');
          const leaveQuery = query(
            leaveRef,
            where('employeeId', '==', user.id),
            where('status', '==', 'Approved')
          );
          const leaveSnapshot = await getDocs(leaveQuery);

          const leaveNotifications = [];
          for (const docSnapshot of leaveSnapshot.docs) {
            const data = docSnapshot.data();
            const notification = `Leave approved from ${data.startDate} to ${data.endDate}`;
            const leaveNotificationRef = collection(db, 'employeeLeaveNotifications');
            const existingLeaveQuery = query(
              leaveNotificationRef,
              where('employeeId', '==', user.id),
              where('notification', '==', notification)
            );
            const existingLeaveSnapshot = await getDocs(existingLeaveQuery);
            if (existingLeaveSnapshot.empty) {
              await addDoc(leaveNotificationRef, {
                employeeId: user.id,
                notification,
                timestamp: serverTimestamp(),
                isRead: false,
              });
              leaveNotifications.push(notification);
            }
          }

          // Fetch Unread Notifications
          const unreadTaskQuery = query(
            collection(db, 'employeeTaskNotifications'),
            where('employeeId', '==', user.id),
            where('isRead', '==', false)
          );
          const unreadLeaveQuery = query(
            collection(db, 'employeeLeaveNotifications'),
            where('employeeId', '==', user.id),
            where('isRead', '==', false)
          );

          const [unreadTaskSnapshot, unreadLeaveSnapshot] = await Promise.all([
            getDocs(unreadTaskQuery),
            getDocs(unreadLeaveQuery),
          ]);

          const unreadNotifications = [
            ...unreadTaskSnapshot.docs.map((doc) => doc.data().notification),
            ...unreadLeaveSnapshot.docs.map((doc) => doc.data().notification),
          ];

          setNotifications(unreadNotifications);
          setUnreadCount(unreadNotifications.length);
        } catch (error) {
          console.error('Error fetching notifications:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchAndSaveNotifications();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Mark Notifications as Read
  const markNotificationsAsRead = async () => {
    try {
      const taskRef = collection(db, 'employeeTaskNotifications');
      const leaveRef = collection(db, 'employeeLeaveNotifications');

      const taskQuery = query(
        taskRef,
        where('employeeId', '==', user.id),
        where('isRead', '==', false)
      );
      const leaveQuery = query(
        leaveRef,
        where('employeeId', '==', user.id),
        where('isRead', '==', false)
      );

      const [taskSnapshot, leaveSnapshot] = await Promise.all([
        getDocs(taskQuery),
        getDocs(leaveQuery),
      ]);

      const updatePromises = [
        ...taskSnapshot.docs.map((doc) => updateDoc(doc.ref, { isRead: true })),
        ...leaveSnapshot.docs.map((doc) => updateDoc(doc.ref, { isRead: true })),
      ];

      await Promise.all(updatePromises);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  return { notifications, loading, unreadCount, markNotificationsAsRead };
};

export default useNotifications;


